import { ReactNode, useState } from 'react'
import {
  AccountBalance,
  Block,
  Calculate,
  Celebration,
  Description,
  HomeWork,
  HourglassTop,
  InfoOutlined,
} from '@mui/icons-material'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import {
  FinancialPerson,
  FinancialProposal,
} from '@/api/financial/application/read-application'
import { MunicipalityOption } from '@/api/financial/person/municipalities'
import { PersonalLoanPreview } from '@/api/financial/simulation/simulate-personal-loan'
import { CepSearchField } from '@/components/address-form/cep-search-field'
import {
  AmountStepper,
  Field,
  MoneyField,
  OptionCards,
  PillGroup,
  ToggleRow,
} from '@/components/credit-journey/fields'
import { DocumentDropzone } from '@/components/document-input/document-dropzone'
import { MaskedInput } from '@/components/masked-input/masked-input'
import { RouterLink } from '@/components/router-link'
import { productPromoLinks } from '@/constants/product-promo'
import { states } from '@/constants/states'
import { BANKS } from '@/pages/credit/banks'
import {
  CivilFields,
  Locked,
  YES_NO,
  brazilDate,
  pct,
  reais,
} from '@/pages/credit/journeys/allowance/allowance-screens'
import { routes } from '@/routes'
import { CepResponse } from '@/types'
import { PersonalLoanStep, documentList } from './personal-loan-flow'
import { EducationKind, PersonalLoanForm, digits } from './personal-loan-form'

const PURPOSES = [
  { value: 'personal_expenses', label: 'Despesas pessoais' },
  {
    value: 'clinic_investment',
    label: 'Investimento em clínica / consultório',
  },
  {
    value: 'specialization_courses',
    label: 'Cursos de especialização, pós ou outros',
  },
  { value: 'pay_debts', label: 'Quitação de dívidas' },
  { value: 'goods_purchase', label: 'Aquisição de bens' },
  { value: 'other', label: 'Outro' },
]

const EDUCATION_STATUS = [
  { value: 'completed', label: 'Concluído' },
  { value: 'in_progress', label: 'Em andamento' },
]

const DOCUMENTS: Record<
  (typeof documentList)[number],
  { label: string; description: string; optional?: boolean }
> = {
  personal: {
    label: 'Documento com foto',
    description:
      'RG, CNH, carteira de trabalho ou passaporte, com CPF. Envie frente e verso.',
  },
  address: {
    label: 'Comprovante de endereço',
    description: 'Do endereço cadastrado, emitido nos últimos 2 meses.',
    optional: true,
  },
  proof_of_income: {
    label: 'Renda dos últimos 3 meses (pessoa física)',
    description:
      'Holerite ou extratos em PDF de todas as contas PF onde recebe salário, plantões e repasses.',
  },
  income_tax_return: {
    label: 'Declaração de imposto de renda',
    description: 'Com o recibo de entrega.',
    optional: true,
  },
}

const MS_PER_DAY = 86_400_000

function daysUntil(iso: string, today = new Date()) {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round(
    (new Date(year, month - 1, day).getTime() - start.getTime()) / MS_PER_DAY,
  )
}

function isoToBr(iso: string) {
  const [year, month, day] = iso.slice(0, 10).split('-')
  return year && month && day ? `${day}/${month}/${year}` : ''
}

function addMonthsBr(iso: string, months: number) {
  const date = new Date(iso)
  date.setMonth(date.getMonth() + months)
  return brazilDate(date.toISOString())
}

function months(value: number) {
  return `${value} meses`
}

function InfoBox({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning'
  children: ReactNode
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: tone === 'warning' ? 'warning.light' : 'action.hover',
        color: tone === 'warning' ? 'warning.contrastText' : 'text.primary',
      }}
    >
      <InfoOutlined color={tone === 'warning' ? 'inherit' : 'primary'} />
      <Typography variant="body2">{children}</Typography>
    </Box>
  )
}

function Rows({
  rows,
  header,
}: {
  rows: [ReactNode, ReactNode][]
  header?: [string, string]
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {header && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 1.75,
            py: 1.5,
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
            fontWeight: 500,
          }}
        >
          <span>{header[0]}</span>
          <span>{header[1]}</span>
        </Box>
      )}
      {rows.map(([label, value], index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 1.75,
            py: 1.5,
            fontSize: 14.5,
            borderTop: index || header ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {label}
          </Box>
          <Box
            component="span"
            sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
          >
            {value}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

function CenterState({
  icon,
  tone = 'primary',
  title,
  children,
}: {
  icon: ReactNode
  tone?: 'primary' | 'success' | 'error'
  title: string
  children: ReactNode
}) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        display: 'grid',
        gap: 1.25,
        justifyItems: 'center',
        py: 3,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: tone === 'primary' ? 'action.hover' : `${tone}.light`,
          color: `${tone}.main`,
          '& svg': { fontSize: 38 },
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6">{title}</Typography>
      <Typography color="text.secondary">{children}</Typography>
    </Box>
  )
}

function AccountingCard({ title, action }: { title: string; action: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '40px minmax(0, 1fr)',
        gap: 1.5,
        p: 2,
        borderRadius: 1,
        color: 'common.white',
        background: (theme) =>
          `linear-gradient(275deg, ${theme.palette.secondary.main} -30%, ${theme.palette.primary.light} 100%)`,
      }}
    >
      <Calculate sx={{ fontSize: 28 }} />
      <Box>
        <Typography sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.25 }}>
          Com a Contabilidade Médica da Alume você paga até 3 vezes menos
          impostos e emite notas fiscais pelo app.
        </Typography>
        <Button
          size="small"
          variant="contained"
          href={productPromoLinks.accountingSpecialist}
          target="_blank"
          rel="noopener"
          sx={{ mt: 1.25, bgcolor: 'common.white', color: 'primary.main' }}
        >
          {action}
        </Button>
      </Box>
    </Box>
  )
}

function EducationFields({
  kind,
  form,
  patch,
}: {
  kind: EducationKind
  form: PersonalLoanForm
  patch: (partial: Partial<PersonalLoanForm>) => void
}) {
  const key = (name: string) => `${kind}_${name}` as keyof PersonalLoanForm
  const value = (name: string) => String(form[key(name)] ?? '')
  const set = (name: string) => (next: string) => patch({ [key(name)]: next })
  return (
    <>
      <TextField
        label="Instituição"
        value={value('institution')}
        onChange={(event) => set('institution')(event.target.value)}
      />
      <TextField
        label="País da instituição"
        value={value('country')}
        onChange={(event) => set('country')(event.target.value)}
      />
      <PillGroup
        label="Status"
        value={value('status')}
        onChange={(next) => set('status')(String(next))}
        options={EDUCATION_STATUS}
      />
      <TextField
        type="month"
        label="Mês/ano de conclusão (ou previsto)"
        value={value('date')}
        onChange={(event) => set('date')(event.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      {kind !== 'g' && (
        <TextField
          label="Especialização"
          value={value('specialty')}
          onChange={(event) => set('specialty')(event.target.value)}
        />
      )}
    </>
  )
}

function Simulation({
  form,
  patch,
  preview,
  loading,
}: {
  form: PersonalLoanForm
  patch: (partial: Partial<PersonalLoanForm>) => void
  preview: PersonalLoanPreview | undefined
  loading: boolean
}) {
  const [detail, setDetail] = useState(false)
  const limits = preview?.limits
  const phase = preview?.summary.phases[0]
  const dueDates = limits?.due_dates ?? []
  const pending = (
    <Skeleton
      variant="text"
      width={80}
      sx={{ display: 'inline-block', fontSize: 'inherit' }}
    />
  )
  const value = (amount: number | undefined) =>
    amount == null || loading ? pending : reais(amount)
  const box = { borderRadius: 1.5, px: 1.75, py: 1.5 }
  const rows: [ReactNode, ReactNode][] = [
    [
      'Taxa de juros',
      preview && !loading
        ? `${pct(preview.summary.monthly_rate)} a.m.`
        : pending,
    ],
    [
      'Custo efetivo total',
      preview && !loading
        ? `${pct(preview.summary.cet_monthly)} a.m.`
        : pending,
    ],
  ]
  if (detail) {
    rows.push(
      ['IOF', value(preview?.summary.iof_total)],
      ['Tarifa de cadastro', value(preview?.registration_fee)],
      ['Valor total de crédito', value(preview?.summary.financed)],
    )
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 2.5, md: 4 },
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
        alignItems: 'start',
      }}
    >
      <Box sx={{ display: 'grid', gap: 3 }}>
        <Box sx={{ display: 'grid', gap: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Valor desejado
          </Typography>
          <AmountStepper
            value={form.amount}
            min={limits?.min ?? 5000}
            max={limits?.max ?? 40000}
            step={limits?.step ?? 1000}
            onChange={(amount) => patch({ amount })}
            label="Valor desejado"
            showEnds
          />
        </Box>
        <Box sx={{ display: 'grid', gap: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Prazo para pagamento
          </Typography>
          <AmountStepper
            value={form.installments}
            min={limits?.min_installments ?? 6}
            max={limits?.max_installments ?? 24}
            step={limits?.installments_step ?? 2}
            onChange={(installments) => patch({ installments })}
            format={months}
            label="Prazo para pagamento"
            stepLabel="2 meses"
            showEnds
          />
        </Box>
        <Field label="Primeira parcela">
          <OptionCards
            value={form.first_due_date}
            onChange={(first_due_date) =>
              patch({ first_due_date: String(first_due_date) })
            }
            options={dueDates.map((date) => ({
              value: date,
              label: isoToBr(date),
              description: `daqui a ${daysUntil(date)} dias`,
            }))}
          />
        </Field>
      </Box>
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}
        >
          <Box sx={{ ...box, bgcolor: 'action.hover' }}>
            <Typography variant="caption">Você recebe</Typography>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {reais(form.amount)}
            </Typography>
            <Typography variant="caption">na sua conta</Typography>
          </Box>
          <Box
            sx={{
              ...box,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
            }}
          >
            <Typography variant="caption">Parcela mensal</Typography>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value(phase?.value)}
            </Typography>
            <Typography variant="caption">
              {form.installments}x
              {form.first_due_date
                ? ` · 1ª em ${isoToBr(form.first_due_date)}`
                : ''}
            </Typography>
          </Box>
        </Box>
        <Rows rows={rows} />
        <Button
          variant="text"
          size="small"
          sx={{ justifySelf: 'start' }}
          onClick={() => setDetail((current) => !current)}
        >
          {detail ? 'Ver menos' : 'Ver mais'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Valores estimados, sujeitos à análise de crédito. Pagando antes ou em
          menos parcelas, o total fica menor.
        </Typography>
      </Box>
    </Box>
  )
}

export function PersonalLoanScreens({
  step,
  form,
  patch,
  preview,
  previewLoading,
  identity,
  municipalities,
  addressState,
  onStateChange,
  onCep,
  documents,
  onUpload,
  onRemove,
  uploadingType,
  phoneVerified,
  denied,
  deniedAt,
  proposal,
  proposalExpired,
  reissuing,
  onReissue,
}: {
  step: PersonalLoanStep
  form: PersonalLoanForm
  patch: (partial: Partial<PersonalLoanForm>) => void
  preview: PersonalLoanPreview | undefined
  previewLoading: boolean
  identity: {
    name: string
    taxId: string
    crm: string
    email: string
    phone: string
  }
  municipalities: MunicipalityOption[]
  addressState: string
  onStateChange: (stateCode: string) => void
  onCep: (cep: CepResponse) => void
  documents: FinancialPerson['documents']
  onUpload: (type: string, file: File) => void
  onRemove: (documentKey: string) => void | Promise<unknown>
  uploadingType?: string
  phoneVerified: boolean
  denied: boolean
  deniedAt: string | null
  proposal: FinancialProposal | null
  proposalExpired: boolean
  reissuing: boolean
  onReissue: () => void
}) {
  const bankLabel = () => {
    const bank = BANKS.find((item) => item.code === form.bank_code)
    const name = bank ? `${bank.code} · ${bank.name}` : form.bank_code
    const agency = form.agency_digit
      ? `${form.agency_number}-${form.agency_digit}`
      : form.agency_number
    return `${name} · ag. ${agency} · conta ${form.account_number}-${form.account_digit}`
  }

  if (step === 'valor') {
    return (
      <Simulation
        form={form}
        patch={patch}
        preview={preview}
        loading={previewLoading}
      />
    )
  }

  if (step === 'eu') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        {!phoneVerified && (
          <Alert
            icon={false}
            severity="warning"
            action={
              <Button
                size="small"
                variant="text"
                color="warning"
                component={RouterLink}
                to={routes.account}
              >
                Verificar telefone
              </Button>
            }
          >
            Cadastre e verifique seu celular no perfil para continuar.
          </Alert>
        )}
        <Locked label="Nome completo" value={identity.name} />
        <Locked label="CPF" value={identity.taxId} />
        <Locked label="CRM" value={identity.crm} />
        <Locked label="E-mail" value={identity.email} />
        <Locked label="Celular" value={identity.phone} />
        <Field label="Data de nascimento">
          <TextField
            type="date"
            value={form.birthdate}
            onChange={(event) => patch({ birthdate: event.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Field>
        <PillGroup
          label="Sexo"
          value={form.gender}
          onChange={(value) => patch({ gender: String(value) })}
          options={[
            { value: 'male', label: 'Masculino' },
            { value: 'female', label: 'Feminino' },
            { value: 'other', label: 'Outro' },
          ]}
        />
      </Box>
    )
  }

  if (step === 'end') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <CepSearchField
          label="CEP"
          value={form.zip_code}
          onChange={(event) => patch({ zip_code: event.target.value })}
          onFindCep={onCep}
        />
        <Locked label="Rua" value={form.street} />
        <Locked label="Bairro" value={form.district} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField
            label="Número"
            value={form.number}
            onChange={(event) => patch({ number: event.target.value })}
          />
          <TextField
            label="Complemento"
            value={form.complement}
            onChange={(event) => patch({ complement: event.target.value })}
          />
        </Box>
        <TextField
          select
          label="Estado"
          value={addressState}
          onChange={(event) => onStateChange(event.target.value)}
        >
          {states.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Cidade"
          value={form.municipality_id}
          onChange={(event) => patch({ municipality_id: event.target.value })}
        >
          {municipalities.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    )
  }

  if (step === 'civil') {
    return <CivilFields form={form} patch={patch} />
  }

  if (step === 'uso') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <MoneyField
          label="Qual sua renda mensal estimada?"
          value={form.income}
          onChange={(income) => patch({ income })}
        />
        <Field label="Como pretende usar o dinheiro?">
          <OptionCards
            multiple
            value={form.purposes}
            onChange={(value) => patch({ purposes: value as string[] })}
            options={PURPOSES}
          />
        </Field>
        <Field label="Descreva um pouco mais os motivos para buscar o crédito - não se preocupe, pois ele é um empréstimo pessoal de uso livre.">
          <TextField
            multiline
            minRows={3}
            value={form.purpose_description}
            onChange={(event) =>
              patch({ purpose_description: event.target.value })
            }
            slotProps={{
              htmlInput: { 'aria-label': 'Motivos para buscar o crédito' },
            }}
          />
        </Field>
        <PillGroup
          label="Você possui uma PJ médica?"
          value={form.has_company}
          onChange={(value) => patch({ has_company: String(value) })}
          options={YES_NO}
        />
        {form.has_company === 'yes' && (
          <Field label="Informe aqui seu CNPJ (obrigatório):">
            <MaskedInput
              mask="00.000.000/0000-00"
              value={form.company_tax_id}
              onChange={(event) =>
                patch({ company_tax_id: event.target.value })
              }
            />
          </Field>
        )}
        {form.has_company !== '' && (
          <PillGroup
            label={
              form.has_company === 'yes'
                ? 'A Alume também é especialista em contabilidade de PJs médicas. Gostaria de receber uma proposta, sem compromisso, para a contabilidade da sua PJ?'
                : 'Alguns hospitais e clínicas só contratam médicos com PJ. Gostaria de receber uma proposta para abrir sua PJ com a Alume?'
            }
            value={form.accounting_proposal}
            onChange={(value) => patch({ accounting_proposal: String(value) })}
            options={YES_NO}
          />
        )}
      </Box>
    )
  }

  if (step === 'grad') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <EducationFields kind="g" form={form} patch={patch} />
      </Box>
    )
  }

  if (step === 'espec') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <ToggleRow
          label="Fiz ou faço residência"
          checked={form.residency}
          onChange={(residency) => patch({ residency })}
        />
        {form.residency && (
          <EducationFields kind="r" form={form} patch={patch} />
        )}
        <ToggleRow
          label="Fiz ou faço pós-graduação"
          checked={form.post}
          onChange={(post) => patch({ post })}
        />
        {form.post && <EducationFields kind="p" form={form} patch={patch} />}
      </Box>
    )
  }

  if (step === 'conta') {
    const bank = BANKS.find((item) => item.code === form.bank_code) ?? null
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Autocomplete
          freeSolo
          options={BANKS}
          value={bank}
          getOptionLabel={(option) =>
            typeof option === 'string'
              ? option
              : `${option.code} · ${option.name}`
          }
          onChange={(_, value) => {
            if (typeof value === 'string') {
              patch({ bank_code: digits(value).slice(0, 3) })
              return
            }
            patch({ bank_code: value?.code ?? '' })
          }}
          renderInput={(params) => <TextField {...params} label="Banco" />}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField
            label="Agência"
            value={form.agency_number}
            onChange={(event) => patch({ agency_number: event.target.value })}
          />
          <TextField
            label="Dígito (opcional)"
            value={form.agency_digit}
            onChange={(event) => patch({ agency_digit: event.target.value })}
          />
          <TextField
            label="Conta"
            value={form.account_number}
            onChange={(event) => patch({ account_number: event.target.value })}
          />
          <TextField
            label="Dígito"
            value={form.account_digit}
            onChange={(event) => patch({ account_digit: event.target.value })}
          />
        </Box>
        <PillGroup
          label="Tipo de conta"
          value={form.account_type}
          onChange={(value) => patch({ account_type: String(value) })}
          options={[
            { value: 'checking', label: 'Conta corrente' },
            { value: 'savings', label: 'Conta poupança' },
          ]}
        />
      </Box>
    )
  }

  if (step === 'docs') {
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <InfoBox>
          <b>Atenção:</b> dê preferência a documentos com verificadores digitais
          de autenticidade.
        </InfoBox>
        <InfoBox tone="warning">
          <b>Não aceitamos extratos PJ.</b> Se você recebe pela empresa, envie o
          extrato da conta PF para onde transfere o pró-labore ou a distribuição
          de lucros.
        </InfoBox>
        {documentList.map((type) => {
          const meta = DOCUMENTS[type]
          const sent = documents.filter((item) => item.type === type)
          return (
            <DocumentDropzone
              key={type}
              label={meta.label}
              description={meta.description}
              optional={meta.optional}
              uploading={uploadingType === type}
              approved={
                sent.length > 0 &&
                sent.every((item) => item.status === 'approved')
              }
              files={sent.map((item) => ({
                key: item.key,
                name: item.original_name,
                isPdf: item.original_name.toLowerCase().endsWith('.pdf'),
              }))}
              onDrop={(picked) =>
                picked.forEach((file) => onUpload(type, file))
              }
              onRemove={onRemove}
            />
          )
        })}
      </Box>
    )
  }

  if (step === 'analise') {
    if (denied) {
      const firstName = identity.name.split(' ')[0]
      return (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <CenterState
            icon={<Block />}
            tone="error"
            title="Não foi aprovado neste momento"
          >
            {firstName ? `${firstName}, com` : 'Com'} base nos dados informados,
            sua solicitação de Crédito para médicos <b>não foi aprovada</b>.{' '}
            {deniedAt
              ? `Você pode fazer uma nova a partir de ${addMonthsBr(deniedAt, 2)}.`
              : 'Você pode fazer uma nova em 2 meses.'}
          </CenterState>
          <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
            Você sabia que tem <b>outras soluções financeiras</b> à sua
            disposição?
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              p: 2,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                fontWeight: 500,
              }}
            >
              <HomeWork color="primary" /> Crédito com imóvel em garantia
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Para imóveis no Sul, Sudeste ou Centro-Oeste. Até 60% do valor do
              imóvel, com juros a partir de 1,09% a.m. + IPCA.
            </Typography>
            <Button
              variant="outlined"
              component={RouterLink}
              to={routes.credit.product.replace(':product', 'home_equity')}
              sx={{ justifySelf: 'start' }}
            >
              Simular crédito
            </Button>
          </Box>
          <AccountingCard title="Contabilidade Médica" action="Saber mais" />
        </Box>
      )
    }
    return (
      <Box sx={{ display: 'grid', gap: 2 }}>
        <CenterState
          icon={<HourglassTop />}
          title="Estamos analisando seus dados"
        >
          Você recebe um retorno em até 2 dias úteis, pelo sino, e-mail e
          WhatsApp.
        </CenterState>
        <AccountingCard
          title="Enquanto isso: atende como PJ?"
          action="Falar sobre PJ Médica"
        />
      </Box>
    )
  }

  if (step === 'proposta') {
    if (!proposal) {
      return <Typography>A proposta ainda não está disponível.</Typography>
    }
    const payment = proposal.cashflow[0]
    const validUntil = brazilDate(proposal.expiration_date)
    return (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Rows
          header={[
            'Crédito para médicos',
            `${pct(Number(proposal.monthly_interest))} a.m.`,
          ]}
          rows={[
            [
              'Valor do empréstimo',
              <b key="amount">{reais(Number(proposal.amount))}</b>,
            ],
            [
              'Forma de pagamento',
              <span key="installments">
                <b>{proposal.installments} parcelas</b> de{' '}
                {reais(payment?.value ?? 0)}
              </span>,
            ],
            ['1ª parcela', payment ? isoToBr(payment.due_date) : '—'],
            ['Válida até', validUntil],
          ]}
        />
        {form.bank_code && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '40px minmax(0, 1fr)',
              gap: 1.5,
              alignItems: 'center',
              p: 1.75,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
            }}
          >
            <AccountBalance color="primary" />
            <Box>
              <Typography sx={{ fontWeight: 500 }}>
                Você recebe na sua conta
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bankLabel()}
              </Typography>
            </Box>
          </Box>
        )}
        {proposalExpired && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={reissuing}
                onClick={onReissue}
              >
                Reemitir proposta expirada
              </Button>
            }
          >
            Esta proposta expirou em {validUntil}.
          </Alert>
        )}
      </Box>
    )
  }

  if (step === 'assinar') {
    return (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Rows
          rows={[
            [
              <Box
                component="span"
                key="ccb"
                sx={{ display: 'inline-flex', gap: 1 }}
              >
                <Description fontSize="small" /> Cédula de Crédito Bancário
              </Box>,
              <Chip
                key="status"
                size="small"
                color="warning"
                label="Pendente"
              />,
            ],
          ]}
        />
        <Typography color="text.secondary">
          A CCB é gerada automaticamente em alguns minutos. Avisamos pelo sino e
          por e-mail quando estiver pronta para assinar. Depois da assinatura,
          enviamos o dinheiro para a sua conta.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <CenterState
        icon={<Celebration />}
        tone="success"
        title="Crédito contratado!"
      >
        Enviamos <b>{reais(Number(proposal?.amount ?? form.amount))}</b> para a
        sua conta.
        {proposal?.cashflow[0]
          ? ` A primeira parcela, de ${reais(proposal.cashflow[0].value)}, vence em ${isoToBr(proposal.cashflow[0].due_date)}.`
          : ''}
      </CenterState>
    </Box>
  )
}
